import {Component, EventEmitter, OnInit, Output, ViewChild} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {Pagination} from "@models/pagination.model";
import {ToastrService} from "ngx-toastr";
import {Patient} from "@shared/api/models/query/patient.model";
import {FormsService} from "@shared/api/services/v2/forms.service";
import {Form, FormResult} from "@shared/api/models/query/form.model";
import {QuestionnaireFormComponent} from "@shared/components/questionnaire-form/questionnaire-form.component";
import {PatientFormResultsService} from "@shared/api/services/v2/patient-form-results.service";
import {PatientFormResultUpdate} from "@shared/api/models/command/patient-form-result.model";
import {PatientFormResult} from "@shared/api/models/query/patient-form-result.model";
import {PatientsService} from "@shared/api/services/v2/patients.service";
import {NgIf} from "@angular/common";
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";

@Component({
    selector: 'patient-registration-ed-and-comorbidities',
    imports: [
        ReactiveFormsModule,
        QuestionnaireFormComponent,
        FormsModule,
        NgIf,
        SpinnerComponent,
    ],
    standalone: true,
    templateUrl: './patient-registration-ed-and-comorbidities.component.html'
})
export class PatientRegistrationEdAndComorbiditiesComponent implements OnInit {
    @ViewChild(QuestionnaireFormComponent) questionnaireFormComponent: QuestionnaireFormComponent
    @Output() output = new EventEmitter<string>();

    loading: boolean = false
    pagination: Pagination = new Pagination()
    typeOfUser: string
    patient: Patient
    form: Form

    constructor(
        private toastr: ToastrService,
        private formsService: FormsService,
        private patientFormResultsService: PatientFormResultsService,
        private patientsService: PatientsService
    ) {
    }

    ngOnInit(): void {

    }

    init(patient: Patient, typeOfUser: string) {
        this.patient = patient
        this.typeOfUser = typeOfUser

        const pagination = new Pagination();
        pagination.filter = {
            type: "co-morbidities",
            scope: "cdss",
            target: typeOfUser
        }
        this.formsService.listPaginated(pagination).subscribe({
            next: response => {
                const targetForm = response.data[0]
                if (targetForm == null) {
                    this.toastr.error('No ED and co-morbidities form is configured for this type of user')
                    return
                }
                this.loadResults(targetForm)
            },
            error: () => {
                this.toastr.error('The ED and co-morbidities form could not be loaded')
            }
        })
    }

    // UNIVAPLAT-2827: display ONLY the co-morbidities form whose target matches the
    // registration type ("Patient" / "Carer" / "Patient and carer"). Each target is a
    // distinct questionnaire for a distinct respondent configuration, so results must
    // never be looked up across targets: a previous fix probed the sibling forms when the
    // target-matched form had no saved results, which made a Carer-type record display
    // (and, on save, write back to) the Patient-target form — a cross-respondent data
    // leak — while issuing extra HTTP requests per probe. typeOfUser is authoritative
    // (patient.type, see patient-edit.page.ts), so an empty result genuinely means no
    // responses are recorded for this respondent and the form must render empty.
    // Exactly two requests per load: the target-filtered form lookup + one results get.
    private loadResults(targetForm: Form) {
        this.patientFormResultsService.get(this.patient.id, targetForm.id).subscribe({
            next: (result?: PatientFormResult) => {
                this.showForm(targetForm, result?.questions)
            },
            error: () => {
                this.showForm(targetForm, null)
            }
        })
    }

    private showForm(form: Form, results?: FormResult[]) {
        this.form = form
        this.questionnaireFormComponent.init(form, results)
    }

    processForm(draft: boolean) {
        if (this.questionnaireFormComponent.isValid()) {
            this.loading = true
            const result = this.questionnaireFormComponent.getResult()
            const data = <PatientFormResultUpdate>{
                patientId: this.patient.id,
                formId: this.form.id,
                questions: result
            }
            this.patientFormResultsService.create(data).subscribe({
                next: () => {
                    this.patientsService.update(this.patient.id, {wizardStep: 3}).subscribe({
                        next: () => {
                            this.loading = false
                            this.output.emit(draft? "draft" : "next")
                        },
                        error: error => {
                            this.loading = false
                            this.toastr.error(error.error.exception, error.error.error);
                        }
                    })
                },
                error: error => {
                    this.loading = false
                    this.toastr.error(error.error.exception, error.error.error);
                }
            });
        } else {
            if (this.questionnaireFormComponent.loadFailed) {
                this.toastr.error('The questionnaire could not be loaded. Please reload the page and try again.');
            } else if (this.questionnaireFormComponent.loading) {
                this.toastr.error('The questionnaire is still loading. Please try again in a moment.');
            } else {
                this.toastr.error('Please provide a value for the required fields');
            }
        }
    }

    protected prev() {
        this.output.emit("prev")
    }
}
